import React, { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield } from "lucide-react";

const sb: any = supabase as any;

type FunctionScore = {
  function_name: string;
  average_score: number;
  maturity_level: string;
  controls_evaluated: number;
};

const FUNCTION_ORDER = ["Govern", "Identify", "Protect", "Detect", "Respond", "Recover"];

const NOT_ASSESSED = "Not Assessed";

const DEFAULT_SCORE: Omit<FunctionScore, "function_name"> = {
  average_score: 0,
  maturity_level: NOT_ASSESSED,
  controls_evaluated: 0,
};

const MATURITY_COLORS: Record<string, string> = {
  "Not Assessed": "#6B7280", // slate-500 — neutral
  Minimal: "#DC2626",
  Adequate: "#F59E0B",
  Strong: "#16A34A",
  Extensive: "#2563EB",
};

type Props = {
  assessmentId: string;
  /** Bump this to trigger a refresh */
  refreshKey?: number;
  /** When true, hide numeric score and controls-evaluated lines. */
  hideNumeric?: boolean;
};

export function NistFunctionScorecard({ assessmentId, refreshKey, hideNumeric }: Props) {
  const [dbScores, setDbScores] = useState<FunctionScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadScores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await sb.rpc("calculate_nist_function_scores", {
        p_assessment_id: assessmentId,
      });
      if (rpcError) throw rpcError;
      setDbScores((Array.isArray(data) ? data : []) as FunctionScore[]);
    } catch (e: any) {
      console.error("[NistFunctionScorecard] load error:", e);
      setError(e.message ?? "Failed to load NIST scores");
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    loadScores();
  }, [loadScores, refreshKey]);

  // Always render all 6 functions, merging DB results with defaults
  const scores = useMemo(() => {
    const map = new Map(dbScores.map((s) => [s.function_name, s]));
    return FUNCTION_ORDER.map((fn) => ({
      ...DEFAULT_SCORE,
      function_name: fn,
      ...(map.get(fn) ?? {}),
    }));
  }, [dbScores]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground text-sm">
          Calculating NIST function scores…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-destructive">
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Shield className="h-4 w-4 text-primary" />
          NIST CSF Functional Ratings
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {scores.map((s) => (
            <div
              key={s.function_name}
              className="rounded-lg border bg-card p-4 flex flex-col items-center gap-1"
            >
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {s.function_name}
              </span>
              <span
                className="text-lg font-bold"
                style={{ color: MATURITY_COLORS[s.maturity_level] ?? "hsl(var(--foreground))" }}
              >
                {s.maturity_level}
              </span>
              {!hideNumeric && s.controls_evaluated > 0 && (
                <>
                  <span className="text-xs text-muted-foreground">
                    Score: <strong className="text-foreground">{Number(s.average_score).toFixed(2)}</strong>
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {s.controls_evaluated} controls evaluated
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
